'use client';

import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faArrowUpArrowDown,
  faCaretDown,
} from '@fortawesome/pro-solid-svg-icons';
import { SortDropdown } from '../SortDropdown';
import { Checkbox } from 'src/components/PulseUI';

interface SortOption {
  label: string;
  value: string;
  order: 'asc' | 'desc';
  metricId?: string;
}

interface SortValues {
  sort: string;
  order: 'asc' | 'desc';
  sort_metric?: string | null;
}

interface StrategyResultsHeaderProps {
  displayCount: number;
  isFetching: boolean;
  search: string;
  setSearch: (search: string) => void;
  sortOptions: SortOption[];
  currentSortLabel: string;
  handleSortChange: (
    field: string,
    order: 'asc' | 'desc',
    metricId?: string
  ) => void;
  sortValues: SortValues;
  strategyMetrics?: any;
  isSortDropdownOpen: boolean;
  setIsSortDropdownOpen: (isOpen: boolean) => void;
  currentPageSelected?: boolean;
  selectedCount?: number;
  onSelectPageChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const StrategyResultsHeader: React.FC<StrategyResultsHeaderProps> = ({
  displayCount,
  isFetching,
  search,
  setSearch,
  sortOptions,
  currentSortLabel,
  handleSortChange,
  sortValues,
  strategyMetrics,
  isSortDropdownOpen,
  setIsSortDropdownOpen,
  currentPageSelected = false,
  selectedCount = 0,
  onSelectPageChange,
}) => {
  return (
    <header
      className="self-stretch p-4 bg-neutral-50 flex flex-col items-start gap-3"
      data-testid="strategy-results-header"
    >
      <div className="self-stretch inline-flex justify-between items-center">
        <div className="flex-1 flex items-center gap-1">
          <h2
            className="text-primary text-lg font-bold leading-7 flex items-center gap-2"
            data-testid="strategy-results-count"
          >
            {isFetching ? (
              <>
                <div
                  className="h-5 w-12 bg-gradient-to-r from-neutral-200 via-neutral-100 to-neutral-200 rounded overflow-hidden relative inline-block"
                  role="status"
                  aria-label="Loading count"
                  data-testid="strategy-results-count-loading"
                >
                  <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
                </div>
                <span>Opportunities</span>
              </>
            ) : (
              `${displayCount} Opportunities`
            )}
          </h2>
        </div>
      </div>
      <div
        className="self-stretch flex items-center gap-1"
        role="search"
        aria-label="Search and sort controls"
      >
        {/* Search Input */}
        <div className="flex-1 min-w-0 h-9 px-3.5 bg-white rounded-full border border-neutral-200 flex items-center gap-1">
          <Icon
            icon={faSearch}
            className="text-secondary w-4 h-4 flex-shrink-0"
            aria-hidden="true"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name..."
            className="flex-1 min-w-0 text-sm font-normal font-['Inter'] leading-tight bg-transparent outline-none placeholder-neutral-500 text-neutral-900"
            aria-label="Search opportunities by name"
            data-testid="strategy-results-search-input"
          />
        </div>
        {/* Sort Dropdown */}
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
            className="h-9 px-3 flex-shrink-0 bg-white rounded-full border border-neutral-200 flex items-center gap-1 cursor-pointer hover:bg-neutral-50 transition-colors"
            aria-label={`Sort by ${currentSortLabel}`}
            aria-expanded={isSortDropdownOpen}
            aria-haspopup="listbox"
            data-testid="strategy-results-sort-button"
          >
            <Icon
              icon={faArrowUpArrowDown}
              className="text-primary w-4 h-4"
              aria-hidden="true"
            />
            <span className="text-sm font-normal text-primary whitespace-nowrap max-w-20 truncate">
              {currentSortLabel}
            </span>
            <Icon
              icon={faCaretDown}
              className={`text-neutral-600 w-2.5 h-1.5 transition-transform ${
                isSortDropdownOpen ? 'rotate-180' : ''
              }`}
              aria-hidden="true"
            />
          </button>
          <SortDropdown
            sortOptions={sortOptions}
            handleSortChange={handleSortChange}
            sortValues={sortValues}
            strategyMetrics={strategyMetrics}
            isOpen={isSortDropdownOpen}
          />
        </div>
      </div>
      {onSelectPageChange && (
        <div
          className="self-stretch h-10 px-4 py-2 bg-neutral-100 rounded-xl border border-neutral-100 inline-flex justify-between items-center"
          data-testid="strategy-results-selection-bar"
        >
          <div className="flex justify-start items-center gap-2.5">
            <Checkbox
              size="sm"
              color="primary"
              checked={currentPageSelected}
              onChange={onSelectPageChange}
              aria-label="Select all results on current page"
              data-testid="strategy-select-all-results-checkbox"
            />
            <label
              className="text-center justify-start text-secondary text-sm font-normal font-['Inter'] leading-tight"
              id="select-page-label"
            >
              Select Page
            </label>
          </div>
          {selectedCount > 0 && (
            <div
              className="text-secondary text-xs font-semibold"
              aria-live="polite"
              data-testid="strategy-selected-providers-count"
            >
              {selectedCount} Selected
            </div>
          )}
        </div>
      )}
    </header>
  );
};

export default StrategyResultsHeader;
